import React, {FC, useEffect, useRef, useState} from "react";
import {DeleteOutlined, EyeOutlined, UploadOutlined} from "@ant-design/icons";
import {Button, Input, Space, Tooltip, App} from "antd";
import {useTranslation} from "react-i18next";
import {buildAuthorizedFileFetchOptions, resolveFileDownloadId} from "../fileDownloadUrl";
import {DeletePopconfirm} from "./DeleteConfirmation";
import {authorizationHeaders} from "../api/authorization";

type FileUploadUrlInputProps = {
    authToken?: string;
    accept?: string;
    disabled?: boolean;
    value?: string;
    onChange?: (value: string) => void;
    uploadText: string;
    clearText: string;
    openText: string;
    emptyText: string;
    uploadSuccessText: string;
    uploadErrorText: string;
};

const normalizeUploadedFileId = (value: unknown): number | undefined => {
    if (typeof value === "number" && Number.isInteger(value) && value > 0) {
        return value;
    }

    if (typeof value === "string" && /^\d+$/.test(value)) {
        const parsed = Number(value);
        return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
    }

    return undefined;
};

const resolveUploadedFileId = (payload: any): number | undefined =>
    normalizeUploadedFileId(payload?.id)
    ?? normalizeUploadedFileId(payload?.data?.id)
    ?? normalizeUploadedFileId(payload?.result?.id)
    ?? normalizeUploadedFileId(payload?.result?.data?.id)
    ?? normalizeUploadedFileId(payload?.value?.id);

const resolveUploadedFileName = (payload: any): string =>
    [payload?.fileName, payload?.data?.fileName, payload?.result?.fileName, payload?.result?.data?.fileName, payload?.value?.fileName]
        .find((candidate): candidate is string => typeof candidate === "string" && !!candidate.trim())
        ?.trim() ?? "";

export const FileUploadUrlInput: FC<FileUploadUrlInputProps> = ({
    authToken,
    accept,
    clearText,
    disabled,
    emptyText,
    onChange,
    openText,
    uploadErrorText,
    uploadSuccessText,
    uploadText,
    value
}) => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const [uploading, setUploading] = useState(false);
    const [displayFile, setDisplayFile] = useState<{value: string; name: string}>();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const openedObjectUrlsRef = useRef<string[]>([]);
    const trimmedValue = value?.trim() ?? "";
    const protectedFileId = resolveFileDownloadId(trimmedValue);
    const displayValue = displayFile?.value === trimmedValue ? displayFile.name : (value ?? "");

    useEffect(() => () => {
        openedObjectUrlsRef.current.forEach(objectUrl => URL.revokeObjectURL(objectUrl));
        openedObjectUrlsRef.current = [];
    }, []);

    useEffect(() => {
        if (!trimmedValue || !protectedFileId) {
            setDisplayFile(undefined);
            return;
        }

        if (displayFile?.value === trimmedValue) {
            return;
        }

        let isCancelled = false;

        fetch(`/api/File/Metadata/${protectedFileId}`, buildAuthorizedFileFetchOptions(authToken))
            .then(response => response.ok ? response.json() : undefined)
            .then(payload => {
                if (isCancelled || !payload) {
                    return;
                }

                const fileName = resolveUploadedFileName(payload);
                if (fileName) {
                    setDisplayFile({
                        name: fileName,
                        value: trimmedValue
                    });
                }
            })
            .catch(() => undefined);

        return () => {
            isCancelled = true;
        };
    }, [authToken, displayFile?.value, protectedFileId, trimmedValue]);

    const handlePickFile = () => {
        if (disabled || uploading) {
            return;
        }

        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        event.target.value = "";

        if (!selectedFile) {
            return;
        }

        const body = new FormData();
        body.append("file", selectedFile);
        setUploading(true);

        try {
            const response = await fetch("/api/File/Upload", {
                method: "POST",
                body,
                credentials: "include",
                headers: authorizationHeaders(authToken)
            });

            if (!response.ok) {
                throw new Error("Upload failed");
            }

            const payload = await response.json();
            const uploadedFileId = resolveUploadedFileId(payload);

            if (!uploadedFileId) {
                throw new Error("Upload response is missing file id");
            }

            const downloadUrl = `/api/File/Download/${uploadedFileId}`;
            setDisplayFile({
                name: resolveUploadedFileName(payload) || selectedFile.name || `file-${uploadedFileId}`,
                value: downloadUrl
            });
            onChange?.(downloadUrl);
            message.success(uploadSuccessText);
        } catch {
            message.error(uploadErrorText);
        } finally {
            setUploading(false);
        }
    };

    const handleOpen = async () => {
        if (!trimmedValue) {
            return;
        }

        if (!protectedFileId) {
            window.open(trimmedValue, "_blank", "noopener,noreferrer");
            return;
        }

        try {
            const response = await fetch(trimmedValue, buildAuthorizedFileFetchOptions(authToken));
            if (!response.ok) {
                throw new Error("Download failed");
            }

            const objectUrl = URL.createObjectURL(await response.blob());
            openedObjectUrlsRef.current.push(objectUrl);
            window.open(objectUrl, "_blank", "noopener,noreferrer");
        } catch {
            window.open(trimmedValue, "_blank", "noopener,noreferrer");
        }
    };

    const hasValue = !!value?.trim();

    return (
        <div className="ati-stack ati-stack--tight">
            <Input
                readOnly
                value={displayValue}
                placeholder={emptyText}
                suffix={
                    <Space size={2}>
                        <Tooltip title={openText}>
                            <Button
                                type="text"
                                size="small"
                                icon={<EyeOutlined />}
                                aria-label={openText}
                                disabled={!hasValue}
                                onClick={handleOpen}
                            />
                        </Tooltip>
                        <Tooltip title={clearText}>
                            <DeletePopconfirm
                                itemType={t("delete-target-file", {defaultValue: "file"})}
                                itemName={displayValue}
                                disabled={!hasValue || disabled || uploading}
                                onConfirm={() => onChange?.("")}
                            >
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    aria-label={clearText}
                                    disabled={!hasValue || disabled || uploading}
                                />
                            </DeletePopconfirm>
                        </Tooltip>
                    </Space>
                }
                addonAfter={
                    <Tooltip title={uploadText}>
                        <Button
                            type="text"
                            size="small"
                            icon={<UploadOutlined />}
                            aria-label={uploadText}
                            loading={uploading}
                            disabled={disabled}
                            onClick={handlePickFile}
                        />
                    </Tooltip>
                }
            />
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                style={{display: "none"}}
                onChange={handleFileChange}
            />
        </div>
    );
};

export default FileUploadUrlInput;
